import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import type {
  StyleMasterPriceItem,
  StyleMasterPriceRow,
} from '@/utils/approval/styleMasterPrice';

/** 조회·작성 화면이 이 클래스로 삭제·부분반려 버튼과 열 제목을 그림 */
export const ACTION_TITLE_CLASS = 'smp-action-title';
export const ACTION_CELL_CLASS = 'smp-action';

const COL_WIDTHS = [
  '10%',
  '5%',
  '8%',
  '10%',
  '15%',
  '7%',
  '7%',
  '7%',
  '8%',
  '15%',
  '8%',
];

const text = (v: unknown) =>
  v === null || v === undefined ? '' : String(v).trim();

/** 레거시 number_format */
const formatNumber = (v: unknown) => {
  const raw = text(v);
  const n = Number(raw.replace(/,/g, ''));
  return raw && Number.isFinite(n)
    ? Math.round(n).toLocaleString('en-US')
    : raw;
};

/** 버튼 칸은 결재 건 단위 — 건의 첫 행에만 두고 그 건 행 수만큼 합침 */
const PriceRow = ({
  item,
  row,
  actionRowSpan,
}: {
  item: StyleMasterPriceItem;
  row: StyleMasterPriceRow;
  actionRowSpan: number;
}) => (
  <tr data-item-id={item.aprvId} data-style-no={item.styleNo}>
    <td className="text-center">{text(row.brandNm)}</td>
    <td className="text-center">{text(row.season)}</td>
    <td className="text-center">{text(row.classLevel1Nm)}</td>
    <td>{text(row.styleNo)}</td>
    <td>{text(row.styleNameKo)}</td>
    {/* 레거시: 변경전 = 현재 마스터가(unitAf*), 변경후 = 요청가(priceUnit*) */}
    <td className="text-right">{formatNumber(row.unitAfKrw)}</td>
    <td className="text-right after">{formatNumber(row.priceUnitKrw)}</td>
    <td className="text-right">{formatNumber(row.unitAfCny)}</td>
    <td className="text-right after">{formatNumber(row.priceUnitCny)}</td>
    <td>{text(row.modCont)}</td>
    {actionRowSpan > 0 && (
      <td
        className={`text-center ${ACTION_CELL_CLASS}`}
        rowSpan={actionRowSpan > 1 ? actionRowSpan : undefined}
      />
    )}
  </tr>
);

/** 레거시 appr_sub_view_forms_pricechage — 소재 타입·성별 열은 레거시처럼 숨김 */
const PriceTable = ({
  items,
  t,
}: {
  items: StyleMasterPriceItem[];
  t: TFunction;
}) => (
  <table className="table table-bordered">
    <colgroup>
      {COL_WIDTHS.map((width, i) => (
        <col key={i} width={width} />
      ))}
    </colgroup>
    <thead>
      <tr>
        <th className="text-center bg-info" colSpan={11}>
          {t('STYLE MASTER 가격 변경 내역')}
        </th>
      </tr>
      <tr>
        <th rowSpan={2}>{t('브랜드')}</th>
        <th rowSpan={2}>{t('시즌')}</th>
        <th rowSpan={2}>{t('대분류')}</th>
        <th rowSpan={2}>Style No.</th>
        <th rowSpan={2}>{t('제품명')}</th>
        <th colSpan={2}>{t('판매가 (원화)')}</th>
        <th colSpan={2}>{t('판매가 (위안)')}</th>
        <th rowSpan={2}>{t('변경사유')}</th>
        <th rowSpan={2} className={ACTION_TITLE_CLASS} />
      </tr>
      <tr>
        <th>{t('변경전')}</th>
        <th className="after">{t('변경후')}</th>
        <th>{t('변경전')}</th>
        <th className="after">{t('변경후')}</th>
      </tr>
    </thead>
    <tbody>
      {items.flatMap((item) =>
        item.rows.map((row, i) => (
          <PriceRow
            key={`${item.aprvId}-${i}`}
            item={item}
            row={row}
            actionRowSpan={i === 0 ? item.rows.length : 0}
          />
        )),
      )}
    </tbody>
  </table>
);

/** 저장 본문 — 고른 순서대로 행 */
export const buildStyleMasterPriceHtml = (
  items: StyleMasterPriceItem[],
  t: TFunction,
): string =>
  items.length ? renderToStaticMarkup(<PriceTable items={items} t={t} />) : '';
